import { useState, useEffect } from 'react';
import { deepLService } from '@/services/deepLService';
import { useLanguageContext } from '@/contexts/LanguageContext';

interface UseDeepLTranslationOptions {
  fallback?: string;
  skipTranslation?: boolean;
  context?: string;
}

export function useDeepLTranslation(text: string, options: UseDeepLTranslationOptions = {}) {
  const { currentLanguage, isTranslationEnabled } = useLanguageContext();
  const active = isTranslationEnabled && !options.skipTranslation && currentLanguage !== 'fr' && !!text?.trim();
  const initial = active ? deepLService.getCached(text, currentLanguage) ?? text : text;
  const [translatedText, setTranslatedText] = useState(initial);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!active) {
      setTranslatedText(text);
      return;
    }
    const cached = deepLService.getCached(text, currentLanguage);
    if (cached) {
      setTranslatedText(cached);
      return;
    }
    setTranslatedText(text);
    setIsLoading(true);
    deepLService
      .translate(text, currentLanguage, { fallback: options.fallback, context: options.context })
      .then((t) => !cancelled && setTranslatedText(t))
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [text, currentLanguage, active, options.fallback, options.context]);

  return { translatedText, isLoading, error: null as string | null, refresh: () => {} };
}

export function useTranslation(text: string, options?: UseDeepLTranslationOptions): string {
  return useDeepLTranslation(text, options).translatedText;
}
